import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { todayBR } from "../lib/dates";
import { Calendar, TrendingUp } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import EntriesTable from "./EntriesTable";

function formatBRL(n) {
  const sign = n < 0 ? "-" : n > 0 ? "+" : "";
  return `${sign}R$ ${Math.abs(n).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Group entries by ISO week
function isoWeek(date) {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

export default function Weekly365Panel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState("current"); // "current" | "all" | "YYYY-Www"

  const load = useCallback(async () => {
    try {
      const res = await api.get("/entries", { params: { weekly_365: true } });
      setEntries(res.data);
    } catch {
      toast.error("Erro ao carregar Semanal 365");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Entrada excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const currentWeek = useMemo(() => isoWeek(todayBR()), []);

  // Filter entries based on selected week (default = current week, resets every Monday)
  const filteredEntries = useMemo(() => {
    if (selectedWeek === "all") return entries;
    const target = selectedWeek === "current" ? currentWeek : selectedWeek;
    return entries.filter((e) => isoWeek(e.date) === target);
  }, [entries, selectedWeek, currentWeek]);

  const totalProfit = filteredEntries.reduce((s, e) => s + (e.profit || 0), 0);
  const totalStake365 = filteredEntries.reduce(
    (s, e) => s + e.bookmakers.filter((b) => b.is_365).reduce((a, b) => a + b.stake, 0),
    0
  );
  const wins = filteredEntries.filter((e) => e.profit > 0).length;
  const winRate = filteredEntries.length ? Math.round((wins / filteredEntries.length) * 1000) / 10 : 0;

  // Weekly buckets
  const byWeek = {};
  for (const e of entries) {
    const w = isoWeek(e.date);
    if (!byWeek[w]) byWeek[w] = { profit: 0, count: 0, wins: 0 };
    byWeek[w].profit += e.profit || 0;
    byWeek[w].count += 1;
    if (e.profit > 0) byWeek[w].wins += 1;
  }
  const weekList = Object.entries(byWeek)
    .map(([w, v]) => ({ week: w, ...v }))
    .sort((a, b) => b.week.localeCompare(a.week));
  const availableWeeks = weekList.map((w) => w.week);

  if (loading) {
    return <div className="h-40 animate-pulse rounded-xl border border-white/10 bg-[#121212]" />;
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[0.75rem] uppercase tracking-widest text-zinc-500">Operações</span>
            <Calendar className="h-4 w-4 text-blue-400" />
          </div>
          <div className="font-mono-num text-3xl font-bold text-white">{filteredEntries.length}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 text-[0.75rem] uppercase tracking-widest text-zinc-500">Lucro total</div>
          <div
            className="font-mono-num text-3xl font-bold"
            style={{ color: totalProfit >= 0 ? "#00FF66" : "#FF3B30" }}
          >
            {formatBRL(totalProfit)}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 text-[0.75rem] uppercase tracking-widest text-zinc-500">Taxa de acerto</div>
          <div className="font-mono-num text-3xl font-bold text-blue-400">{winRate.toFixed(1)}%</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 text-[0.75rem] uppercase tracking-widest text-zinc-500">
            Investido na 365
          </div>
          <div className="font-mono-num text-3xl font-bold text-zinc-300">
            R$ {totalStake365.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Semanal 365</h2>
          <p className="text-xs text-zinc-500">
            {selectedWeek === "all"
              ? "Todas as operações marcadas como Semanal 365"
              : selectedWeek === "current"
              ? `Semana atual (${currentWeek}) · acumulado zerado toda segunda-feira`
              : `Semana ${selectedWeek}`}
          </p>
        </div>
        <Select value={selectedWeek} onValueChange={setSelectedWeek}>
          <SelectTrigger data-testid="weekly-365-week-filter" className="h-9 w-[220px] border-white/10 bg-[#121212] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] border-white/10 bg-[#121212] text-zinc-200">
            <SelectItem value="current">Semana atual ({currentWeek})</SelectItem>
            <SelectItem value="all">Todas semanas</SelectItem>
            {availableWeeks.filter((w) => w !== currentWeek).map((w) => (
              <SelectItem key={w} value={w}>{w}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Weekly buckets */}
      {weekList.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-[#121212] p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-bold text-white">Resumo por semana</h3>
              <p className="text-xs text-zinc-500">Agrupado pela semana ISO</p>
            </div>
            <TrendingUp className="h-4 w-4 text-zinc-500" />
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {weekList.map((w) => {
              const positive = w.profit >= 0;
              const wr = w.count ? Math.round((w.wins / w.count) * 100) : 0;
              return (
                <div
                  key={w.week}
                  data-testid={`week-bucket-${w.week}`}
                  className="rounded-lg border border-white/10 bg-black/40 p-3"
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-mono-num text-xs text-zinc-400">{w.week}</span>
                    <span className="text-[0.7rem] uppercase tracking-widest text-zinc-600">
                      {w.count} ops
                    </span>
                  </div>
                  <div
                    className="font-mono-num text-lg font-bold"
                    style={{ color: positive ? "#00FF66" : "#FF3B30" }}
                  >
                    {formatBRL(w.profit)}
                  </div>
                  <div className="text-[0.7rem] text-zinc-500">acerto {wr}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <EntriesTable entries={filteredEntries} onEdit={() => {}} onDelete={handleDelete} />
    </div>
  );
}
